import { FC, ReactNode, useEffect, useState } from 'react'
import styled from 'styled-components'
import { Select } from '@lmc/ui-kit/src/dataEntry'
import { InputNumber } from 'antd'
import { Tag } from 'antd'
import Text from '../../general/Typography/Text/Text'
import { FormItem } from '../Form/FormItem'
import { Frequency, ShortDay } from '@lmc/ts-common'
import dayjs from 'dayjs'
import updateLocale from 'dayjs/plugin/updateLocale'

export const getEveryLabel = (frequency?: string) => {
  if (frequency === Frequency.MONTHLY) {
    return 'Month'
  }

  if (frequency === Frequency.WEEKLY) {
    return 'Week'
  }

  return 'Day'
}

export type FrequencyInputProps = {
  label?: ReactNode
  onChangeFrequency?: (value?: string) => void
  frequencyValue?: string
  onChangeDays?: (value?: string[]) => void
  specificDaysValue?: string[]
  onChangeIntervals?: (value?: number) => void
  intervalValue?: number
  hasError?: boolean
  monthlyOptions?: { label: string; value: string | number }[]
  optimisticWeekStartDate?: number
}

export enum DAYS_TYPE_TITLE {
  MONDAY = 'M',
  TUESDAY = 'Tu',
  WEDNESDAY = 'W',
  THURSDAY = 'Th',
  FRIDAY = 'F',
  SATURDAY = 'Sa',
  SUNDAY = 'Su',
}

export const daysOptions = [
  { value: ShortDay.Mon, label: DAYS_TYPE_TITLE.MONDAY },
  { value: ShortDay.Tue, label: DAYS_TYPE_TITLE.TUESDAY },
  { value: ShortDay.Wed, label: DAYS_TYPE_TITLE.WEDNESDAY },
  { value: ShortDay.Thu, label: DAYS_TYPE_TITLE.THURSDAY },
  { value: ShortDay.Fri, label: DAYS_TYPE_TITLE.FRIDAY },
  { value: ShortDay.Sat, label: DAYS_TYPE_TITLE.SATURDAY },
  { value: ShortDay.Sun, label: DAYS_TYPE_TITLE.SUNDAY },
]

const FrequencyInput: FC<FrequencyInputProps> = ({
  label = 'Frequency',
  onChangeFrequency,
  onChangeDays,
  onChangeIntervals,
  frequencyValue,
  intervalValue,
  specificDaysValue,
  hasError = false,
  optimisticWeekStartDate,
}) => {
  const frequencyOptions = [
    { label: 'Daily', value: Frequency.DAILY },
    { label: 'Weekly', value: Frequency.WEEKLY },
    { label: 'Monthly', value: Frequency.MONTHLY },
  ]

  const [selectedFrequency, setSelectedFrequency] = useState<
    string | undefined
  >(frequencyValue || Frequency.DAILY)

  const [selectedInterval, setSelectedInterval] = useState<number | undefined>(
    intervalValue || 1
  )

  const daysOptionsValues = daysOptions?.map((item) => item?.value)

  const [selectedDays, setSelectedDays] = useState<string[] | undefined>(
    specificDaysValue || daysOptionsValues
  )

  const handleChangeFrequencyType = (value: string) => {
    setSelectedFrequency(value as Frequency)
    if (selectedDays?.length !== 7 && value === Frequency.DAILY) {
      setSelectedDays(daysOptionsValues)
    }
    if (value === Frequency.WEEKLY && !!optimisticWeekStartDate) {
      setSelectedDays([daysOptionsValues[optimisticWeekStartDate - 1]])
    }
  }

  const handleChangeInterval = (value?: unknown) => {
    setSelectedInterval((value as number) || 1)
  }

  const handleChangeDay = (tag: string, checked: boolean) => {
    const nextSelectedDays = checked
      ? [...(selectedDays || []), tag]
      : (selectedDays || []).filter((t) => t !== tag)
    setSelectedDays(nextSelectedDays)
    if (nextSelectedDays?.length !== 7) {
      setSelectedFrequency(Frequency.WEEKLY)
    } else {
      setSelectedFrequency(Frequency.DAILY)
    }
  }

  useEffect(() => {
    onChangeFrequency?.(selectedFrequency)
  }, [selectedFrequency])

  useEffect(() => {
    onChangeDays?.(selectedDays)
  }, [selectedDays])

  const hasOneInterval = selectedInterval === 1

  useEffect(() => {
    onChangeIntervals?.(selectedInterval)
  }, [selectedInterval])

  const showDaysPicker =
    (selectedFrequency === Frequency.DAILY && hasOneInterval) ||
    selectedFrequency === Frequency.WEEKLY

  return (
    <FormItem
      label={
        <StyledText>
          <Asterix> * </Asterix>
          {label}
        </StyledText>
      }
    >
      <FormItem
        validateStatus={hasError ? 'error' : undefined}
        extra={
          hasError ? (
            <StyledErrorText>The Frequency field is required</StyledErrorText>
          ) : undefined
        }
      >
        <Select
          options={frequencyOptions}
          placeholder="Please select"
          onChange={(value) => handleChangeFrequencyType(value)}
          value={selectedFrequency}
        />
      </FormItem>
      {[Frequency.DAILY, Frequency.WEEKLY, Frequency.MONTHLY]?.includes(
        selectedFrequency as Frequency
      ) && (
        <EveryDaysWrapper>
          <StyledText>Every</StyledText>
          <StyledInput
            onChange={handleChangeInterval}
            value={selectedInterval}
          />
          <StyledText>{getEveryLabel(frequencyValue)}(s)</StyledText>
        </EveryDaysWrapper>
      )}
      {showDaysPicker && (
        <CheckableOuterWrapper>
          <StyledText>
            <Asterix> * </Asterix>Specific days:
          </StyledText>
          <CheckableWrapper>
            {daysOptions.map((tag) => (
              <CheckableTag
                key={tag.value}
                checked={!!selectedDays?.includes(tag.value)}
                onChange={(checked) => handleChangeDay(tag.value, checked)}
                style={{
                  color: selectedDays?.includes(tag.value) ? 'white' : 'black',
                }}
              >
                {tag.label}
              </CheckableTag>
            ))}
          </CheckableWrapper>
        </CheckableOuterWrapper>
      )}
    </FormItem>
  )
}

const CheckableTag = styled(Tag.CheckableTag)`
  border-radius: 50%;
  width: 32px;
  height: 32px;
  align-items: center;
  display: flex;
  justify-content: center;
  font-size: 16px;
`

const StyledInput = styled(InputNumber)`
  margin-left: 8px;
  margin-right: 8px;
`

const StyledText = styled(Text)`
  font-size: 16px;
  color: ${({ theme }) => theme.color.neutral.color4};
`

const CheckableWrapper = styled.div`
  display: flex;
  margin-top: 8px;
`

const Asterix = styled.a`
  color: #ff4d4f;
`

const StyledErrorText = styled(Text)`
  color: #ff4d4f;
  font-size: 12px;
`

const CheckableOuterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 24px;
`

const EveryDaysWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-top: 24px;
`

/* const StyleRadioGroup = styled(FormRadioGroup)`
  .ant-radio-wrapper {
    width: unset;
    background-color: transparent !important;
  }
`

const StyledFormItem = styled(FormItem)`
  margin-top: 24px;
` */

export default FrequencyInput

dayjs.extend(updateLocale)
dayjs.updateLocale('en', {
  weekStart: 1,
})
